<template>
  <div class="benefits-page">
    <page-layout>
      <section slot="header">
        <transition name="fade" mode="out-in">
          <header-user-account v-if="Object.keys(userInfo).length"
                               key="header"
                               :header-info="userInfo"/>
          <div v-else
               key="headerPlaceholder"
               class="header-placeholder"></div>
        </transition>
      </section>
      <transition name="fade" mode="out-in">
        <loader-with-message v-if="dataLoading"
                             key="loader"
                             :has-error="dataFetchError"
                             @retry="fetchBenefits"/>
        <section v-else class="page-content" key="page-content">
          <transition name="fade" mode="out-in">
            <account-status-blocked v-if="userStatus === 'BLOCKED'"
                                    key="status-blocked"
                                    :resources-data="resourcesData"/>
            <account-status-on-hold v-else-if="userStatus === 'ON_HOLD'"
                                    key="status-on-hold"
                                    :resources-data="resourcesData"/>
            <div v-else
                 key="status-active">
              <base-heading>{{ $t('benefits.benefitsForYou') }}</base-heading>
              <p class="page-description" v-html="$t('benefits.pageDescription')"></p>
              <benefits-item :title="userPoints > 0 ? $t('benefits.totalPointsScored') :  $t('benefits.noPointsLabel')"
                             :totalPoints="userPoints > 0 ? userPoints : null"/>
              <div class="points-info">
                <benefits-item :title="$t('benefits.pointsHistory')"
                               @learn-more="showHistoryModal = true"
                               :disabled="!benefitsHistory.length"/>
                <benefits-item :title="$t('benefits.howToGetPoints')"
                               @learn-more="showInfoModal = true"/>
              </div>
              <template v-if="availableOffers.length">
                <p class="benefits-title">{{ $t('benefits.title') }}</p>
                <template v-for="(offer, index) in availableOffers">
                  <benefits-item :points="offer.points"
                                 :title="offer.name"
                                 :description="offer.description ? offer.description : ''"
                                 :key="'offer'+index"
                                 :canActivate="offer.activable"
                                 @activate-benefit="openActivateBenefitModal(offer)"/>
                </template>
              </template>
            </div>
          </transition>
        </section>
      </transition>

      <activate-benefit-modal :visible="activateBenefitModalVisible"
                              :benefit-name="selectedBenefitData.name"
                              :benefit-type="selectedBenefitData.type"
                              :mobile-services="mobileServices"
                              :tv-services="tvServices"
                              :loading="benefitSubmitLoading"
                              @submit-common-benefit="activateCommonAddon"
                              @submit-topup-benefit="activateTopUpAddon"
                              @close="closeActivateBenefitModal"/>
    </page-layout>
    <!-- info modal - how to earn points -->
    <Modal :title="$t('benefits.howToGetPoints')" size="lg" :visible="showInfoModal"
           @modal-close="showInfoModal = false"
           class="info-modal">
      <div v-for="(item, index) in availableActivities" class="info-modal-item" :key="'item'+index">
        <span class="info-modal-item-name">{{ item.name }}</span>
        <span class="info-modal-item-points">
          {{ item.points }}
          <span class="text">{{ pointsText(item.points) }}</span>
        </span>
      </div>
    </Modal>
    <!-- info modal - points history -->
    <Modal :title="$t('benefits.pointsHistoryTitle')"
           size="lg"
           :visible="showHistoryModal"
           @modal-close="showHistoryModal = false"
           class="info-modal">
      <div class="modal-scroll-area" v-bar="{preventParentScroll: true, scrollThrottle: 15, scrollingPhantomDelay: 200}">
        <div class="inner-scroll">
          <div v-for="(item, index) in benefitsHistory" class="info-modal-item" :key="'history'+index">
            <p class="info-modal-item-name">
              {{ item.name }}
              <span class="info-modal-item-description">
                {{ item.creationtime }}
              </span>
            </p>
            <p class="info-modal-item-points" :class="{'green': item.action}">
              {{ (item.action ? '+' : '-') + item.points }}
              <span class="text">{{ pointsText(item.points) }}</span>
            </p>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import PageLayout from '@/components/common/main-layout/page-layout'
import HeaderUserAccount from '@/components/userAccount/headers/header-user-account'
import BenefitsItem from '@/components/benefits/benefits-item'
import LoaderWithMessage from '@/components/common/loader-with-message'
import AccountStatusBlocked from '@/components/services/account-status/account-status-blocked'
import AccountStatusOnHold from '@/components/services/account-status/account-status-on-hold'
import Modal from '@/components/common/modal/modal'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import ActivateBenefitModal from '@/components/benefits/activate-benefit-modal'
import { addDataLayerEvent } from '@/services/dataLayer'
import { addUserIdDataLayerEvent } from '@/utils/addUserIdDataLayerEvent'

export default {
  name: 'page-benefits',
  components: {
    ActivateBenefitModal,
    PageLayout,
    HeaderUserAccount,
    BenefitsItem,
    LoaderWithMessage,
    AccountStatusBlocked,
    AccountStatusOnHold,
    Modal
  },
  mixins: [errorMessageHandler],
  data () {
    return {
      dataLoading: true,
      dataFetchError: false,
      userInfo: {},
      availableOffers: [],
      availableActivities: [],
      benefitsHistory: [],
      userPoints: 0,
      userStatus: '',
      resourcesData: [],
      activateBenefitModalVisible: false,
      selectedBenefitData: {},
      benefitSubmitLoading: false,
      showInfoModal: false,
      showHistoryModal: false,
      tvServices: [],
      mobileServices: []
    }
  },
  methods: {
    fetchUserInfo () {
      this.$apiRequest.get('user/info')
        .then(response => {
          this.userInfo = response.data
          this.resourcesData = response.data.mtmCustomers[0] || []
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    fetchBenefits () {
      this.$apiRequest.get('user/benefits')
        .then(response => {
          this.availableOffers = response.data.availableOffers || []
          this.availableActivities = response.data.availableActivities || []
          this.benefitsHistory = response.data.histories || []
          this.userPoints = response.data.customerPoints || 0
          this.userStatus = response.data.contractStatus
          this.dataLoading = false
        })
        .catch(e => {
          this.dataFetchError = true
        })
    },
    openActivateBenefitModal (benefitData) {
      this.activateBenefitModalVisible = true
      this.selectedBenefitData = { ...benefitData }
      if (benefitData.type === 'TV_ADDON') {
        this.fetchTvServices()
      } else if (benefitData.type === 'MOBILE_ADDON') {
        this.fetchMobileServices()
      }
    },
    fetchTvServices (forceFetch = false) {
      if (!this.tvServices.length || forceFetch) {
        this.benefitSubmitLoading = true
        this.$apiRequest.get('user/services/filter?serviceType=TV')
          .then(response => {
            this.tvServices = response.data.services || []
          })
          .catch(e => {
            this.handleErrorMessage(e.response.data)
          })
          .finally(() => {
            this.benefitSubmitLoading = false
          })
      }
    },
    fetchMobileServices (forceFetch = false) {
      if (!this.mobileServices.length || forceFetch) {
        this.benefitSubmitLoading = true
        this.$apiRequest.get('user/services/filter?serviceType=MOBILE')
          .then(response => {
            this.mobileServices = response.data.services || []
          })
          .catch(e => {
            this.handleErrorMessage(e.response.data)
          })
          .finally(() => {
            this.benefitSubmitLoading = false
          })
      }
    },
    activateCommonAddon (subscriberId) {
      const payload = {
        subscriberIdentity: subscriberId,
        addonExternalId: this.selectedBenefitData.externalId,
        activate: true,
        addonTargetType: 'BENEFIT'
      }

      this.benefitSubmitLoading = true
      this.$apiRequest.post('user/services/addons', payload)
        .then(() => {
          this.toast('success', {
            title: this.$t('toastMessages.successToastTitle'),
            message: this.$t('benefits.activationModal.activationSuccess')
          })
          this.fetchBenefits()

          addDataLayerEvent({
            name: this.selectedBenefitData.name,
            points: this.selectedBenefitData.points,
            event: 'benefit_activation'
          })

          setTimeout(() => {
            this.closeActivateBenefitModal()
          }, 0)
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.benefitSubmitLoading = false
        })
    },
    activateTopUpAddon (phoneNumber) {
      const normalizedNumber = phoneNumber.replace(/[^0-9]/g, '')
      const payload = {
        benefitCode: this.selectedBenefitData.benefitCode,
        toMsisdn: normalizedNumber
      }

      this.benefitSubmitLoading = true

      this.$apiRequest.post('user/mobileservice/topup/benefits', payload)
        .then(() => {
          this.toast('success', {
            title: this.$t('toastMessages.successToastTitle'),
            message: this.$t('benefits.activationModal.activationSuccess')
          })
          this.fetchBenefits()
          addDataLayerEvent({
            name: this.selectedBenefitData.name,
            points: this.selectedBenefitData.points,
            event: 'benefit_activation'
          })
          setTimeout(() => {
            this.closeActivateBenefitModal()
          }, 0)
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.benefitSubmitLoading = false
        })
    },
    closeActivateBenefitModal () {
      this.activateBenefitModalVisible = false
      this.selectedBenefitData = {}
    },
    pointsText (points) {
      const lastDigit = points % 10
      return points === 11 || lastDigit !== 1 ? this.$t('benefits.points') : this.$t('benefits.point')
    }
  },
  created () {
    this.fetchUserInfo()
    this.fetchBenefits()
    addDataLayerEvent({
      event: 'screen',
      name: 'benefits'
    })
    addUserIdDataLayerEvent()
  }
}
</script>

<style scoped lang="scss">
/deep/ {
  .page-header .page-header-inner {
    @media (map_get($breakpoints, md)) {
      padding: 0;
    }
  }

  > .page-content {
    padding: 2rem;

    @media (map_get($breakpoints, sm)) {
      padding: 4rem 2rem;
      max-width: none;
    }

    @media (map_get($breakpoints, md)) {
      padding: 4rem 0;
      max-width: 960px;
    }

    @media (map_get($breakpoints, lg)) {
      max-width: 1240px;
    }

    @media (map_get($breakpoints, xxl)) {
      max-width: 1600px;
    }
  }
}

.page-description {
  margin-bottom: 3rem;
  max-width: 54rem;
  font-size: 1.4rem
}

.benefits-title {
  margin-bottom: .8rem;
  color: $dark-grey;

  @media (map_get($breakpoints, sm)) {
    margin-bottom: 1.5rem;
  }
}

.points-info {
  margin-bottom: 3rem;

  @media (map_get($breakpoints, sm)) {
    display: flex;
    margin: 0 -.4rem 4rem;
  }

  .benefits-item {
    @media (map_get($breakpoints, sm)) {
      margin: 0 .4rem;
      width: calc(50% - .8rem);
      max-width: calc(50% - .8rem);
    }
  }
}

.info-modal {
  padding: 4rem 2.4rem;

  @media (map_get($breakpoints, sm)) {
    padding: 0 .5rem;
  }

  /deep/ {
    .custom-modal__container {
      position: relative;
      padding: 3rem 1.6rem 1rem;

      @media (map_get($breakpoints, sm)) {
        padding: 4rem 4rem 2rem;
      }
    }

    .custom-modal__header--close {
      position: absolute;
      top: 1.5rem;
      right: 1rem;

      @media (map_get($breakpoints, sm)) {
        top: 5rem;
        right: 3.5rem;
      }
    }

    .custom-modal__header--slot {
      font-size: 2rem;
      font-weight: 500;
      text-align: center;

      @media (map_get($breakpoints, sm)) {
        font-size: 3.2rem;
        text-align: left;
      }
    }
  }

  .info-modal-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.6rem 0;
    font-weight: bold;
    margin-right: 1rem;

    &:not(:last-child) {
      border-bottom: 1px solid $grey;
    }

    @media (map_get($breakpoints, sm)) {
      margin-right: 0;
    }
  }

  .info-modal-item-name {
    font-size: 1.4rem;
    flex: 1 1 auto;

    @media (map_get($breakpoints, sm)) {
      font-size: 1.6rem;
    }
  }

  .info-modal-item-description {
    display: block;
    font-size: 1.4rem;
    font-weight: normal;
    color: $dark-grey;
    margin-top: 0.4rem;
  }

  .info-modal-item-points {
    color: $primary;
    font-size: 2.4rem;
    text-align: right;
    white-space: nowrap;

    &.green {
      color: $success;
    }

    .text {
      position: relative;
      top: -.3rem;
      font-size: 1.2rem;
    }
  }

  .modal-scroll-area {
    max-height: 480px;
    max-width: calc(100% + 1rem);
    margin-right: -1rem;

    @media (map_get($breakpoints, sm)) {
      margin-right: -3rem;
    }

    .inner-scroll {
      max-height: 480px;
      padding-right: 3rem !important;
      max-width: calc(100% - 1rem);

      @media (map_get($breakpoints, sm)) {
        max-width: unset;
      }
    }
  }
}
</style>
